<template>
    <div class="user-groups fh">
      <div class="user-groups-bar v-center space-between px-5">
        <div class="v-center">
          <img src="~@/assets/images/icons/arrow-back.svg" class="mr-3 pointer" width="30px" height="20px" alt="" @click="$router.go(-1)"/>
          <h1 class="weight-600 xl">
            <input
              class="name-input weight-600 xl"
              v-model="newGroup.name"
              autofocus
            />
          </h1>
        </div>
        <div class="v-center">
          <router-link to="/settings/user-groups">
            <button class="btn btn-cancel mr-3 pointer">Cancel</button>
          </router-link>
          <button class="btn pointer" @click="saveUserGroup" :disabled="disableSave">Save</button>
        </div>
      </div>
      <div class="user-groups-container">
        <div class="user-groups-description px-5 mt-3 input-group">
            <label for="description" class="l">Description:</label>
          <textarea
            v-model="newGroup.description"
            placeholder="Description"
            class="form-control"
            rows="3"
          >
          </textarea>
        </div>

        <!-- Dual Select Container -->
        <div class="dual-select-container">
          <div class="select-box">
            <h3>Available Users</h3>
            <input
              class="search-box"
              v-model="searchAvailable"
              placeholder="Search users..."
            />
            <ul>
              <li
                v-for="user in filteredAvailableUsers"
                :key="user.id"
                @click="selectUser(user)"
              >
                <div class="user-info">
                  <span class="user-name">{{ user.name }}</span>
                  <span class="user-details"
                    >{{ user.email }} | {{ user.role }}</span
                  >
                </div>
                <span class="add-icon">
                  <img
                    src="~@/assets/images/icons/plus-icon.svg"
                    width="20px"
                    alt=""
                  />
                </span>
              </li>
            </ul>
          </div>
          <div class="select-box">
            <h3>Selected Users</h3>
            <input
              class="search-box"
              v-model="searchSelected"
              placeholder="Search users..."
            />
            <ul>
              <li
                v-for="user in filteredSelectedUsers"
                :key="user.id"
                @click="deselectUser(user)"
              >
                <div class="user-info">
                  <span class="user-name">{{ user.name }}</span>
                  <span class="user-details"
                    >{{ user.email }} | {{ user.role }}</span
                  >
                </div>
                <span class="remove-icon">
                  <img
                    src="~@/assets/images/icons/close-icon.svg"
                    width="20px"
                    alt=""
                  />
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
import { mapGetters } from 'vuex'
import { CreateNewUserGroup, findExistingUserGroup, restoreUserGroup } from '@/api'
import { alert, success } from '@/plugins/notification'
import Config from '@/config.js'
import confirmationDialog from '@/plugins/confirmationDialog'

export default {
  name: 'user-groups-create',
  data: () => ({
    loading: false,
    newGroup: {
      name: 'New User Group',
      description: '',
      core_user_group_members: []
    },
    searchAvailable: '',
    searchSelected: '',
    availableUsers: [],
    selectedUsers: [],
    disableSave: false
  }),
  computed: {
    ...mapGetters(['tenantUsersList']),
    filteredAvailableUsers () {
      return this.availableUsers.filter(
        (user) =>
          user?.name
            ?.toLowerCase()
            .includes(this.searchAvailable.toLowerCase()) ||
          user?.email
            ?.toLowerCase()
            .includes(this.searchAvailable.toLowerCase()) ||
          user?.role?.toLowerCase().includes(this.searchAvailable.toLowerCase())
      )
    },
    filteredSelectedUsers () {
      return this.selectedUsers.filter(
        (user) =>
          user?.name?.toLowerCase().includes(this.searchSelected.toLowerCase()) ||
          user?.email
            ?.toLowerCase()
            .includes(this.searchSelected.toLowerCase()) ||
          user?.role?.toLowerCase().includes(this.searchSelected.toLowerCase())
      )
    }
  },
  methods: {
    validateData () {
      if (!this.newGroup.name.length) {
        alert('Please enter a name for the user group')
        return false
      }
      if (!this.selectedUsers.length) {
        alert('Please select at least one user')
        return false
      }
      return true
    },
    async saveUserGroup () {
      try {
        if (!this.validateData()) return
        this.disableSave = true
        const data = this.selectedUsers.map((user) => {
          return { user_id: user.id }
        })
        this.newGroup.core_user_group_members = { data }
        const res = await findExistingUserGroup(this.newGroup.name)
        if (res.core_user_group.length) {
          if (res.core_user_group[0].deleted) {
            confirmationDialog(
              'There is a deleted user group with the same name. Do you want to restore it?',
              (res) => {
                if (res) {
                  restoreUserGroup(this.newGroup.name).then(
                    () => {
                      success('User group restored successfully')
                      this.$router.push('/settings/user-groups')
                    }).catch(() => {
                    alert('Unable to restore user group')
                  })
                }
              }
            )
          } else {
            alert('User group name already exists')
          }
        } else {
          await CreateNewUserGroup(this.newGroup)
          success('User group created successfully')
          this.$router.push('/settings/user-groups')
        }
      } catch (err) {
        if (err.message.includes('Uniqueness violation')) {
          alert('User group name already exists')
        } else {
          alert('Unable to create user group')
        }
      } finally {
        this.disableSave = false
      }
    },
    selectUser (user) {
      this.selectedUsers.push(user)
      this.availableUsers = this.availableUsers.filter((u) => u.id !== user.id)
    },
    deselectUser (user) {
      this.availableUsers.push(user)
      this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id)
    }
  },
  mounted () {
    for (const user of this.tenantUsersList) {
      if ((user.status === Config.USER_STATUS_MAP.ACTIVE ||
      (user.status === Config.USER_STATUS_MAP.INVITED && user?.associated_user.status === Config.USER_STATUS_MAP.ACTIVE)) &&
      user.associated_role?.id !== Config.USER_ROLE_MAP.VIEWER) {
        this.availableUsers.push({
          id: user.associated_user.id,
          name: `${user.associated_user.first_name} ${user.associated_user.last_name}`,
          email: user.associated_user.email,
          role: user.associated_role.name
        })
      }
    }
  }
}
</script>

  <style lang="scss" scoped>
  textarea {
    width: 100%;
    height: 50px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 10px;
    font-size: var(--text-l);
    font-family: Arial, sans-serif;
    outline: none;
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease-in-out;
    resize: none;
  }
  textarea:focus {
    border-color: var(--bg-color);
    box-shadow: 2px 2px 10px rgba(0, 123, 255, 0.2);
  }
  .user-groups {
    &-bar {
      height: 60px;
      margin-bottom: 0;
      background: var(--bg-color);
      border-bottom: var(--border);
    }
    .name-input {
      border: none;
      border-bottom: solid 0.5px rgba(0, 0, 0, 0.7);
      outline: none;
      padding: 5px;
      max-width: 100%;
      background: transparent;
      &:focus {
        border: solid 0.5px rgba(0, 0, 0, 0.7);
        border-radius: 5px;
      }
    }
    .dual-select-container {
      display: flex;
      justify-content: space-between;
      padding: 20px;
      gap: 20px;
    }
    .select-box {
      width: 50%;
      border: 1px solid #ddd;
      padding: 15px;
      border-radius: 12px;
      background: #fefefe;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }
    .search-box {
      width: 100%;
      padding: 8px;
      margin-bottom: 10px;
      border: 1px solid #ccc;
      border-radius: 6px;
      outline: none;
    }
    h3 {
      text-align: center;
      margin-bottom: 10px;
      font-weight: 600;
      color: #333;
    }
    ul {
      list-style: none;
      padding: 0;
      height: calc(100vh - 450px);
      overflow-y: auto;
    }
    li {
      padding: 12px;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      transition: background 0.2s ease-in-out;
    }
    li:hover {
      background: #f5f5f5;
    }
    .user-info {
      display: flex;
      flex-direction: column;
      line-height: 1.4;
    }
    .user-name {
      font-weight: 600;
      color: #222;
    }
    .user-details {
      font-size: var(--text-s);
      color: #666;
    }
    .add-icon,
    .remove-icon {
      font-size: var(--text-base);
      padding: 4px 8px;
      border-radius: 6px;
      background: #e0e0e0;
      transition: background 0.2s ease-in-out;
    }
    .add-icon:hover {
      background: #4caf50;
      color: white;
    }
    .remove-icon:hover {
      background: #d32f2f;
      color: white;
    }
  }
  </style>
